import posthog from "posthog-js";
import { axiosBasic } from "../api/axios";
import { config } from "../api/endPoints";
import { getSelectedCompany, getSelectedOffice, removeSpace } from "../components/Global/CustomFunctions";
import { LocalStorage } from "../components/Global";
import { filterActiveOffices } from "./workplaceUtils";

const { Office } = config.REST_API;

/** @returns {Record<string, unknown> | null} */
export const getUserFromStorage = () => {
  try {
    const raw = localStorage.userInfo;
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
};

const persistSelectedOffice = (officeObj) => {
  const officeJson = JSON.stringify(officeObj);
  localStorage.setItem(LocalStorage.selectedOffice, officeJson);
  sessionStorage.setItem("selectedOffice", officeJson);
};

/**
 * Navigate to the wall chart (team time off) for the user's workplace.
 * @param {import('react-router-dom').History} history
 * @param {Record<string, unknown> | null} [user]
 */
export const navigateToWallChart = async (history, user = getUserFromStorage()) => {
  if (!history) return;

  const selectedCompany = getSelectedCompany();
  if (!selectedCompany || !selectedCompany.name) {
    history.push("/wallchart");
    return;
  }

  try {
    const response = await axiosBasic.get(Office.CurrentUserAssociatedWithWorkPlace);
    const { success, data } = response.data;

    if (success && data && data.officeName) {
      const officeNameFromApi = String(data.officeName).trim();
      let officeObj = { name: officeNameFromApi };

      if (selectedCompany.id) {
        try {
          const officesRes = await axiosBasic.get(`${Office.GetCompanyOffices}?companyId=${selectedCompany.id}`);
          const { success: ok, data: offices } = officesRes.data;
          if (ok && Array.isArray(offices) && offices.length > 0) {
            const match = offices.find(
              (o) => (o.name || "").trim().toLowerCase() === officeNameFromApi.toLowerCase()
            );
            if (match) {
              officeObj = {
                id: match.id,
                name: match.name,
                country: match.country || match.countryName || "",
              };
            }
          }
        } catch {
          /* keep officeObj with name only */
        }
      }

      persistSelectedOffice(officeObj);
      const companyName = removeSpace(selectedCompany.name.toLowerCase());
      const officeName = removeSpace(officeNameFromApi.toLowerCase());
      history.push(`/${companyName}/${officeName}/dashboard/time-off`);
      return;
    }
  } catch (error) {
    if (typeof posthog !== "undefined" && posthog.capture) {
      posthog.capture("headerFetchWorkplaces", {
        type: "exception",
        success: false,
        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        status: error?.response?.status,
      });
    }
  }

  if (selectedCompany.id) {
    try {
      const response = await axiosBasic.get(`${Office.GetCompanyOffices}?companyId=${selectedCompany.id}`);
      const { success, data } = response.data;

      if (success && Array.isArray(data) && data.length > 0) {
        const activeOffices = filterActiveOffices(data);
        if (activeOffices.length > 0) {
          const userOfficeId = user?.officeId ?? user?.OfficeId;
          const selectedOffice = getSelectedOffice();
          const workplace =
            activeOffices.find((o) => o.id === userOfficeId || o.id === Number(userOfficeId)) ||
            (selectedOffice &&
              activeOffices.find((o) => o.id === selectedOffice.id || o.id === selectedOffice.Id)) ||
            activeOffices[0];

          persistSelectedOffice({
            id: workplace.id,
            name: workplace.name,
            country: workplace.country || workplace.countryName || "",
          });

          const companyName = removeSpace(selectedCompany.name.toLowerCase());
          const officeName = removeSpace(workplace.name.toLowerCase());
          history.push(`/${companyName}/${officeName}/dashboard/time-off`);
          return;
        }
      }
    } catch {
      /* fallback below */
    }
  }

  const selectedOffice = getSelectedOffice();
  if (selectedCompany && selectedOffice) {
    const companyName = removeSpace(selectedCompany.name.toLowerCase());
    const officeName = removeSpace(selectedOffice.name.toLowerCase());
    history.push(`/${companyName}/${officeName}/dashboard/time-off`);
    return;
  }

  history.push("/wallchart");
};

/**
 * Navigate to the current user's leave year calendar.
 * @param {import('react-router-dom').History} history
 * @param {Record<string, unknown> | null} [user]
 */
export const navigateToMyCalendar = async (history, user = getUserFromStorage()) => {
  if (!history) return;

  const currentUserId = user?.selfUserId;
  if (!currentUserId) {
    if (typeof posthog !== "undefined" && posthog.capture) {
      posthog.capture("headerMyCalendar", {
        type: "exception",
        success: false,
        reason: "User ID not available",
      });
    }
    return;
  }

  const selectedCompany = getSelectedCompany();
  const selectedOffice = getSelectedOffice();
  const calendarState = {
    employeeId: currentUserId,
    employeeName: user?.name || null,
    employeeImageUrl: user?.imageUrl || null,
  };

  if (selectedCompany && selectedOffice) {
    const companyNameParam = removeSpace(selectedCompany.name.toLowerCase());
    const officeNameParam = removeSpace(selectedOffice.name.toLowerCase());
    history.push({
      pathname: `/${companyNameParam}/${officeNameParam}/dashboard/calender`,
      state: calendarState,
    });
    return;
  }

  if (selectedCompany?.id) {
    try {
      const response = await axiosBasic.get(`${Office.GetCompanyOffices}?companyId=${selectedCompany.id}`);
      const { success, data } = response.data;

      if (success && Array.isArray(data) && data.length > 0) {
        const activeOffices = filterActiveOffices(data);
        if (activeOffices.length > 0) {
          const userOfficeId = user?.officeId ?? user?.OfficeId;
          const workplace =
            activeOffices.find((o) => o.id === userOfficeId || o.id === Number(userOfficeId)) ||
            activeOffices[0];

          persistSelectedOffice({
            id: workplace.id,
            name: workplace.name,
            country: workplace.country || workplace.countryName || "",
          });

          const companyNameParam = removeSpace(selectedCompany.name.toLowerCase());
          const officeNameParam = removeSpace(workplace.name.toLowerCase());
          history.push({
            pathname: `/${companyNameParam}/${officeNameParam}/dashboard/calender`,
            state: calendarState,
          });
          return;
        }
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("headerFetchWorkplaces", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
    }
  }

  history.push("/wallchart");
};
